import {
  API_BATCH_CONCURRENCY,
  BATCH_SIZE_DEFAULT,
  BATCH_SIZE_LARGE_LABEL,
  BATCH_SIZE_LARGE_SLIP,
  BATCH_SIZE_THRESHOLD_LARGE,
} from "@client-shared/lib/constants";
import { runSequentially } from "@shared/lib/concurrency/run-sequentially";
import { extractErrorMessage, getErrorStatusCode, isRetryableError } from "./errorUtils";

/**
 * Batch processing utilities for ShippingDocumentsDialog
 */

export type OrderId = { purchaseOrderId: string; batchId?: string };
export type BulkShippingLabelUrlsFn = (orderIds: OrderId[], signal?: AbortSignal) => Promise<Record<string, string | null>>;

/**
 * Determines batch size based on dataset size
 * Labels can use slightly larger batches since label PDFs are smaller than packing slips
 */
export const getBatchSize = (itemCount: number, isLabel: boolean = false): number => {
  if (itemCount > BATCH_SIZE_THRESHOLD_LARGE) {
    return isLabel ? BATCH_SIZE_LARGE_LABEL : BATCH_SIZE_LARGE_SLIP;
  }
  return BATCH_SIZE_DEFAULT;
};

/**
 * Creates a failed URL map for a batch of orders
 */
const createFailedUrlMap = (batch: OrderId[]): Record<string, string | null> => {
  const failedMap: Record<string, string | null> = {};
  for (const orderId of batch) {
    failedMap[orderId.purchaseOrderId] = null;
  }
  return failedMap;
};

/**
 * Checks if the operation was aborted by the signal
 */
const isOperationAborted = (signal?: AbortSignal, error?: unknown): boolean => {
  if (signal?.aborted) {
    return true;
  }
  if (error instanceof Error && error.name === "AbortError") {
    return true;
  }
  return false;
};

/**
 * Handles retry logic with exponential backoff
 */
const handleRetryDelay = async (
  retryCount: number,
  batchNumber: number,
  statusCode: number | undefined,
  batchError: unknown
): Promise<void> => {
  const delayMs = Math.pow(2, retryCount) * 1000;
  const errorMessage = extractErrorMessage(batchError);
  console.warn(
    `Retryable error (${statusCode ?? "network"}) fetching shipping label URLs for batch ${batchNumber}, retrying (${retryCount + 1}/3) after ${delayMs}ms:`,
    errorMessage
  );
  await new Promise((resolve) => setTimeout(resolve, delayMs));
};

/**
 * Logs non-retryable error
 */
const logNonRetryableError = (batchNumber: number, statusCode: number | undefined, batchError: unknown): void => {
  const errorMessage = extractErrorMessage(batchError);
  const statusCodeStr = statusCode ? ` (HTTP ${statusCode})` : "";
  console.error(`Failed to fetch shipping label URLs for batch ${batchNumber}${statusCodeStr}:`, errorMessage);
};

type BatchResult = { success: boolean; urlMap: Record<string, string | null>; batch: OrderId[] };
type AttemptBatchResult = { shouldRetry: true } | { shouldRetry: false; result: BatchResult };

/**
 * Attempts to fetch batch URLs with error handling
 */
const attemptBatchFetch = async (
  batch: OrderId[],
  batchNumber: number,
  getBulkShippingLabelUrls: BulkShippingLabelUrlsFn,
  signal: AbortSignal | undefined,
  retryCount: number,
  maxRetries: number
): Promise<AttemptBatchResult> => {
  try {
    const batchUrlMap = await getBulkShippingLabelUrls(batch, signal);
    return { shouldRetry: false, result: { success: true, urlMap: batchUrlMap, batch } };
  } catch (batchError) {
    // Don't treat abort as an error
    if (isOperationAborted(signal, batchError)) {
      return { shouldRetry: false, result: { success: false, urlMap: createFailedUrlMap(batch), batch } };
    }

    // Check if error is retryable
    const isRetryable = isRetryableError(batchError);
    const statusCode = getErrorStatusCode(batchError);

    if (isRetryable && retryCount < maxRetries) {
      // Exponential backoff: 1s, 2s, 4s
      await handleRetryDelay(retryCount, batchNumber, statusCode, batchError);
      if (isOperationAborted(signal)) {
        return { shouldRetry: false, result: { success: false, urlMap: createFailedUrlMap(batch), batch } };
      }
      return { shouldRetry: true };
    }

    logNonRetryableError(batchNumber, statusCode, batchError);
    return { shouldRetry: false, result: { success: false, urlMap: createFailedUrlMap(batch), batch } };
  }
};

/**
 * Processes a single batch of order IDs to fetch shipping label URLs
 * Includes retry logic for retryable errors (502, 503, 504, etc.) with exponential backoff
 */
export const processBatch = async (
  batch: OrderId[],
  batchNumber: number,
  getBulkShippingLabelUrls: BulkShippingLabelUrlsFn,
  signal?: AbortSignal,
  maxRetries: number = 3
): Promise<{
  success: boolean;
  urlMap: Record<string, string | null>;
  batch: OrderId[];
}> => {
  if (isOperationAborted(signal)) {
    return { success: false, urlMap: createFailedUrlMap(batch), batch };
  }

  let retryCount = 0;

  while (retryCount <= maxRetries) {
    const attemptResult = await attemptBatchFetch(batch, batchNumber, getBulkShippingLabelUrls, signal, retryCount, maxRetries);

    if (!attemptResult.shouldRetry) {
      return attemptResult.result;
    }

    retryCount++;
  }

  // Fallback (should not reach here due to loop logic)
  return { success: false, urlMap: createFailedUrlMap(batch), batch };
};

/**
 * Processes batch results and yields URLs in order
 */
export const processBatchResults = function* (
  results: Array<{
    success: boolean;
    urlMap: Record<string, string | null>;
    batch: OrderId[];
  }>,
  urlMap: Record<string, string | null>
): Generator<string, void, unknown> {
  for (const result of results) {
    Object.assign(urlMap, result.urlMap);
    for (const orderId of result.batch) {
      const url = result.urlMap[orderId.purchaseOrderId];
      if (url) {
        yield url;
      }
    }
  }
};

/**
 * Processes a batch of orders for label URLs
 * Wrapper around processBatch that returns result without the batch field
 */
export const processLabelUrlBatch = async (
  batch: OrderId[],
  batchNumber: number,
  getBulkShippingLabelUrls: BulkShippingLabelUrlsFn,
  signal?: AbortSignal
): Promise<{ success: boolean; urlMap: Record<string, string | null> }> => {
  const result = await processBatch(batch, batchNumber, getBulkShippingLabelUrls, signal);
  return { success: result.success, urlMap: result.urlMap };
};

/**
 * Processes all batches and builds URL map
 */
export const processAllLabelUrlBatches = async (
  batches: OrderId[][],
  getBulkShippingLabelUrls: BulkShippingLabelUrlsFn,
  signal?: AbortSignal
): Promise<{ urlMap: Record<string, string | null>; hasErrors: boolean }> => {
  const urlMap: Record<string, string | null> = {};
  let hasBatchErrors = false;

  // Check if aborted before starting
  if (signal?.aborted) {
    return { urlMap, hasErrors: false };
  }

  // Process batches with concurrency limit: groups of batches run one after another to bound API concurrency.
  const groupStarts: number[] = [];
  for (let i = 0; i < batches.length; i += API_BATCH_CONCURRENCY) {
    groupStarts.push(i);
  }
  await runSequentially(groupStarts, async (i) => {
    // Check if aborted before each batch group; once aborted, every remaining group is skipped
    if (signal?.aborted) {
      return;
    }

    const concurrentBatches = batches.slice(i, i + API_BATCH_CONCURRENCY);
    const batchPromises = concurrentBatches.map((batch, batchIndex) => {
      const batchNumber = i + batchIndex + 1;
      return processLabelUrlBatch(batch, batchNumber, getBulkShippingLabelUrls, signal);
    });

    const results = await Promise.all(batchPromises);
    hasBatchErrors = hasBatchErrors || results.some((r) => !r.success);
    // Merge all results into urlMap
    for (const result of results) {
      Object.assign(urlMap, result.urlMap);
    }
  });

  return { urlMap, hasErrors: hasBatchErrors };
};
